import type { ButtonHTMLAttributes, ReactNode } from 'react';
import React from 'react';
import type { ButtonTypeMap, Theme } from '@material-ui/core';
import { Button, CircularProgress, Icon } from '@material-ui/core';

import type { KlpStyled } from '../Theme';
import { removeKlpStyles } from '../Theme';
import classNames from 'classnames';
import type { WithTranslation } from '../TranslationOrChildren';
import { TranslationOrChildren } from '../TranslationOrChildren';
import { useKlpStyles } from '../Theme/useKlpStyles';
import { Illustration } from '../Illustrations';
import type {
    CommonProps,
    OverridableTypeMap,
} from '@material-ui/core/OverridableComponent';

export enum ActionButtonType {
    back = 'back',
    cancel = 'cancel',
    forward = 'forward',
    button = 'button',
    submit = 'submit',
    save = 'save',
    external = 'external',
    download = 'download',
}

export type ActionButtonProps = Partial<ButtonTypeMap['props']> &
    ButtonHTMLAttributes<HTMLButtonElement> &
    CommonProps<OverridableTypeMap> &
    WithTranslation &
    KlpStyled & {
        actionType: keyof typeof ActionButtonType;
        target?: string;
        forwardedRef?: React.RefObject<HTMLButtonElement>;
        isLoading?: boolean;
    };

const styles = (theme: Theme) => ({
    button: {
        display: 'flex',
        marginLeft: theme.spacing(0.5),
        marginRight: theme.spacing(0.5),
        '&:first-of-type': {
            marginLeft: 0,
        },
        '&:last-of-type': {
            marginRight: 0,
        },
    },
    disabledButton: {
        cursor: 'not-allowed',
    },
});

const iconMapStart: Partial<Record<ActionButtonType, string>> = {
    back: 'arrow_back',
    cancel: 'clear',
};

const iconMapEnd: Partial<Record<ActionButtonType, string>> = {
    submit: 'check',
    save: 'check',
    external: 'open_in_new_sharp',
    download: 'get_app',
    forward: 'arrow_forward',
};

const colorMap: Partial<
    Record<ActionButtonType, 'primary' | 'secondary' | 'default'>
> = {
    cancel: 'secondary',
};

const isReactNode = (node): node is ReactNode => React.isValidElement(node);

export const ActionButton: React.FC<
    React.PropsWithChildren<ActionButtonProps>
> = (props) => {
    const {
        className,
        actionType,
        translation,
        translationOptions,
        disabled,
        onClick,
        startIcon,
        endIcon,
        forwardedRef,
        children,
        isLoading = false,
        ...rest
    } = props;

    const classes = useKlpStyles(props, styles);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
        onClick?.(event);
    };

    const getIcon = (
        icon: string | ReactNode | undefined,
        type: keyof typeof ActionButtonType,
        position: 'start' | 'end',
    ) => {
        if (isReactNode(icon)) return icon;
        if (type === 'button' && icon) return <Illustration icon={icon} />;
        if (position === 'start' && iconMapStart[type]) {
            return <Icon>{iconMapStart[type]}</Icon>;
        }
        if (position === 'end') {
            if (iconMapEnd[type]) {
                return <Icon>{iconMapEnd[type]}</Icon>;
            }
        }
        return null;
    };

    return (
        <Button
            ref={forwardedRef}
            variant="outlined"
            color={colorMap[actionType] || rest.color || 'primary'}
            startIcon={getIcon(startIcon, actionType, 'start')}
            endIcon={
                isLoading ? (
                    <CircularProgress size={16} />
                ) : (
                    getIcon(endIcon, actionType, 'end')
                )
            }
            onClick={!disabled ? handleClick : undefined}
            className={classNames(
                classes.button,
                classes.klpStyles,
                className,
                {
                    [classes.disabledButton]: disabled,
                },
            )}
            disabled={disabled || isLoading}
            type={actionType === 'submit' ? 'submit' : 'button'}
            {...removeKlpStyles(rest)}
        >
            <TranslationOrChildren
                translation={translation}
                translationOptions={translationOptions}
            >
                {children}
            </TranslationOrChildren>
        </Button>
    );
};
